import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Building2, ClipboardList, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ReadOnlyBanner, HighValueBadge, DetailHeader, Section, DL, inr, fmtDate, safe, arr } from "./shared";

const EST_STATUS_CFG = {
  draft: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Draft" },
  prepared: { cls: "bg-[#457b9d]/15 text-[#1d3557] border-[#457b9d]/30", label: "Prepared" },
  approved: { cls: "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25", label: "Approved" },
  partial: { cls: "bg-[#F79009]/15 text-[#B54708] border-[#F79009]/30", label: "Partial" },
  rejected: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Rejected" },
  converted: { cls: "bg-[#F1FAEE] text-[#027A48] border-[#12B76A]/25", label: "Converted" },
  expired: { cls: "bg-[#e63946]/10 text-[#e63946] border-[#e63946]/25", label: "Expired" },
  cancelled: { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: "Cancelled" },
};
const EstChip = ({ s }) => {
  const c = EST_STATUS_CFG[s] || { cls: "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]", label: s || "—" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold ${c.cls}`}>{c.label}</span>;
};

const LineTable = ({ title, rows, columns }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-2">{title}</div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-[12px] border border-[#E4E7EC] rounded-md">
          <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
            <tr>{columns.map((c) => <th key={c.k} className={`px-3 py-2 ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {rows.map((r, i) => (
              <tr key={r.id || i}>
                {columns.map((c) => (
                  <td key={c.k} className={`px-3 py-2 ${c.align === "right" ? "text-right font-mono" : ""}`}>
                    {c.render ? c.render(r) : safe(r[c.k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function AdminEstimateDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/ops/estimates/${id}`);
        if (live) setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [id]);

  if (loading) return <div className="p-10 text-center text-[#667085]">Loading estimate…</div>;
  if (!data) return <div className="p-10 text-center text-[#e63946]">Estimate not found.</div>;

  const e = data.estimate;
  const totals = e.totals || {};
  const grand = Number(totals.grand_total || e.total || 0);
  const highValue = grand >= 100000;
  const garage = data.garage || {};
  const customer = data.customer || {};
  const cs = e.customer_snapshot || {};
  const vs = e.vehicle_snapshot || {};
  const linked = data.linked || {};

  return (
    <div className="space-y-4 max-w-6xl" data-testid="admin-est-detail">
      <DetailHeader
        crumb="Back to Estimates"
        backTo="/admin/estimates"
        title={e.number || `EST ${(e.id || "").slice(0, 8)}`}
        subtitle={`${garage.name || "—"} · ${fmtDate(e.estimate_date || e.created_at)}`}
        right={
          <>
            <EstChip s={e.status} />
            <HighValueBadge show={highValue} />
            <button onClick={() => window.print()} className="h-8 px-3 rounded-md border border-[#E4E7EC] bg-white text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5">
              <Printer size={13} /> Print
            </button>
          </>
        }
      />

      <ReadOnlyBanner />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Grand Total</div>
          <div className="text-[20px] font-bold text-[#1d3557] font-mono mt-0.5">{inr(grand)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Estimate Date</div>
          <div className="text-[14px] font-semibold text-[#1d3557] mt-0.5">{fmtDate(e.estimate_date)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Valid Until</div>
          <div className="text-[14px] font-semibold text-[#1d3557] mt-0.5">{fmtDate(e.valid_until)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Prepared By</div>
          <div className="text-[14px] font-semibold text-[#1d3557] mt-0.5">{safe(e.prepared_by_name || e.prepared_by)}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {garage.id && <Link to={`/admin/garages/${garage.id}`} className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"><Building2 size={13} /> Open Garage</Link>}
        {linked.job_card && <Link to={`/admin/job-cards/${linked.job_card.id}`} className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#457b9d] hover:border-[#457b9d] inline-flex items-center gap-1.5"><ClipboardList size={13} /> Job Card {linked.job_card.number}</Link>}
      </div>

      <Section title="Customer Details" testId="est-customer-section">
        <DL cols={3} pairs={[
          ["Name", customer.name || cs.name],
          ["Mobile", customer.phone || cs.phone],
          ["Email", customer.email || cs.email],
          ["Address", customer.address || cs.address],
        ]} />
      </Section>

      <Section title="Vehicle Details" testId="est-vehicle-section">
        <DL cols={3} pairs={[
          ["Registration", vs.reg_number],
          ["Make", vs.make],
          ["Model", vs.model],
          ["Variant", vs.variant],
          ["VIN", vs.vin],
        ]} />
      </Section>

      {arr(e.complaints).length > 0 && (
        <Section title="Customer Complaints" testId="est-complaints-section">
          <ul className="space-y-1.5">
            {arr(e.complaints).map((c, i) => (
              <li key={c?.id || i} className="text-[13px] text-[#1d3557] pl-3 border-l-2 border-[#457b9d]/40">{safe(typeof c === "string" ? c : (c?.text || c?.complaint || c))}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section title="Line Items" testId="est-lines-section">
        <div className="space-y-6">
          <LineTable
            title="Parts"
            rows={arr(e.parts)}
            columns={[
              { k: "name", label: "Part" },
              { k: "part_number", label: "Part #" },
              { k: "description", label: "Description" },
              { k: "qty", label: "Qty", align: "right", render: (r) => Number(r.qty || 0) },
              { k: "unit_price", label: "Rate", align: "right", render: (r) => inr(r.unit_price || r.rate) },
              { k: "discount", label: "Discount", align: "right", render: (r) => inr(r.discount) },
              { k: "gst_rate", label: "GST%", align: "right", render: (r) => `${r.gst_rate || 0}%` },
              { k: "total", label: "Total", align: "right", render: (r) => inr(r.total || r.line_total) },
            ]}
          />
          <LineTable
            title="Labour"
            rows={arr(e.labor || e.labour)}
            columns={[
              { k: "name", label: "Task" },
              { k: "description", label: "Description" },
              { k: "qty", label: "Qty", align: "right", render: (r) => Number(r.qty || 0) },
              { k: "rate", label: "Rate", align: "right", render: (r) => inr(r.rate || r.unit_price) },
              { k: "gst_rate", label: "GST%", align: "right", render: (r) => `${r.gst_rate || 0}%` },
              { k: "total", label: "Total", align: "right", render: (r) => inr(r.total || r.line_total) },
            ]}
          />
          <LineTable
            title="Other Charges"
            rows={arr(e.other_charges)}
            columns={[
              { k: "name", label: "Charge" },
              { k: "description", label: "Description" },
              { k: "amount", label: "Amount", align: "right", render: (r) => inr(r.amount || r.total) },
            ]}
          />
          {/* Fallback for legacy schema where line items are on `e.lines` */}
          <LineTable
            title="Line Items (legacy)"
            rows={arr(e.lines)}
            columns={[
              { k: "kind", label: "Kind" },
              { k: "name", label: "Item" },
              { k: "part_number", label: "Part #" },
              { k: "qty", label: "Qty", align: "right", render: (r) => Number(r.qty || 0) },
              { k: "rate", label: "Rate", align: "right", render: (r) => inr(r.rate || r.unit_price) },
              { k: "total", label: "Total", align: "right", render: (r) => inr(r.total || r.line_total) },
            ]}
          />
        </div>
      </Section>

      <Section title="Summary" testId="est-summary-section">
        <DL cols={2} pairs={[
          ["Parts Subtotal", inr(totals.parts_subtotal || totals.parts_total)],
          ["Labour Subtotal", inr(totals.labour_subtotal || totals.labor_subtotal || totals.labour_total)],
          ["Other Charges", inr(totals.other_subtotal || totals.charges_total)],
          ["Discount", inr(totals.discount_amount || totals.discount)],
          ["Taxable", inr(totals.taxable)],
          ["CGST", inr(totals.cgst)],
          ["SGST", inr(totals.sgst)],
          ["IGST", inr(totals.igst)],
          ["Round-off", inr(totals.round_off)],
          ["Grand Total", inr(grand)],
        ]} />
      </Section>

      {arr(e.terms).length > 0 && (
        <Section title="Terms & Conditions" testId="est-terms-section">
          <ul className="space-y-1.5">
            {arr(e.terms).map((t, i) => <li key={i} className="text-[13px] text-[#1d3557]">• {safe(t)}</li>)}
          </ul>
        </Section>
      )}

      {e.notes && (
        <Section title="Notes" testId="est-notes-section">
          <div className="text-[13px] text-[#1d3557] whitespace-pre-line">{safe(e.notes)}</div>
        </Section>
      )}
    </div>
  );
}
